import { type FC } from 'react'
import { Stack } from '@mui/material'
import dayjs from 'dayjs'

import { Body2, H6, ListItem, ListItemIcon, ListItemText, Skeleton } from '@dhl-ui/components'
import { ChevronRightIcon } from '@dhl-ui/icons'
import { AddressLines } from '@dhl/components'

import { ServicepointDistanceChip } from './ServicepointDistanceChip.js'
import { type ServicepointListItemProps } from './ServicepointListItem.js'
import { getFirstOpeningsTime } from './utils.js'
import { ServicepointOpeningTimesChip } from './ServicepointOpeningTimesChip.js'
import { ServicepointIcon } from './ServicepointIcon.js'

type Props = Omit<ServicepointListItemProps, 'isDesktop'>

export const ServicepointListItemMobile: FC<Props> = ({
  servicepoint,
  divider,
  onClick,
  children,
  ref,
  showOpeningTimes = true,
}) => {
  const firstOpeningTime = getFirstOpeningsTime(servicepoint, dayjs())

  const handleServicePointClick = () => {
    onClick?.(servicepoint)
  }

  if (!firstOpeningTime) {
    return null
  }

  return (
    <ListItem
      isButton
      alignItems="center"
      hasDivider={divider}
      data-test="servicepoint-list-item"
      data-testid="servicepoint-list-item"
      slotProps={{
        secondaryIcon: <ChevronRightIcon />,
      }}
      onClick={handleServicePointClick}
    >
      <ListItemIcon isBlock>
        <ServicepointIcon
          servicepointFormat={servicepoint.servicepointFormat}
          servicepointType={servicepoint.servicepointType}
        />
      </ListItemIcon>
      <ListItemText data-test={servicepoint.servicepointKey}>
        <Stack spacing={1} mt={1}>
          {(servicepoint.distance || showOpeningTimes) && (
            <Stack direction="row" spacing={0.5}>
              <ServicepointDistanceChip distance={servicepoint.distance ?? 0} size="small" />
              {showOpeningTimes && <ServicepointOpeningTimesChip firstOpeningTime={firstOpeningTime} size="small" />}
            </Stack>
          )}
          <Stack direction="row" alignItems="center" ref={ref} spacing={1}>
            <H6>{servicepoint.name}</H6>
          </Stack>
        </Stack>

        <Body2>
          <AddressLines singleLine address={servicepoint.address} />
        </Body2>
      </ListItemText>
      {children}
    </ListItem>
  )
}

export const ServicepointListItemMobileSkeleton: FC<{ divider?: boolean }> = ({ divider = true }) => (
  <ListItem alignItems="center" hasDivider={divider}>
    <ListItemIcon isBlock>
      <Skeleton variant="rounded" width={24} height={24} />
    </ListItemIcon>
    <ListItemText>
      <Skeleton variant="rounded" width={64} height={20} />
      <Stack mt={1}>
        <Stack direction="row" spacing={0.5}>
          <Skeleton variant="text" textVariant="h6" width={80} />
          <Skeleton variant="text" textVariant="h6" width={120} />
        </Stack>
        <Skeleton variant="text" textVariant="body2" width={250} />
      </Stack>
    </ListItemText>
  </ListItem>
)
